import { useCallback, useLayoutEffect, useRef, type UIEventHandler } from 'react';

const NEAR_LATEST_THRESHOLD_PX = 96;

interface ChatAutoScrollOptions {
  threadKey: string;
  itemCount: number;
  busy?: boolean;
}

/**
 * Keeps a back-and-forth conversation anchored to its latest turn without
 * fighting someone who intentionally scrolls upward to review older context.
 *
 * - Initial load: jump to the latest turn immediately (no animated page jump).
 * - New content: stay pinned only when the reader was already near the bottom.
 * - Explicit send: callers can force the thread back to the latest turn.
 */
export function useChatAutoScroll({ threadKey, itemCount, busy = false }: ChatAutoScrollOptions) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const currentThreadRef = useRef(threadKey);
  const positionedRef = useRef(false);
  const pinnedToLatestRef = useRef(true);

  const scrollToLatest = useCallback((behavior: ScrollBehavior = 'auto') => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
    pinnedToLatestRef.current = true;
  }, []);

  const onScroll = useCallback<UIEventHandler<HTMLDivElement>>((event) => {
    const viewport = event.currentTarget;
    const distanceFromLatest = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    pinnedToLatestRef.current = distanceFromLatest <= NEAR_LATEST_THRESHOLD_PX;
  }, []);

  useLayoutEffect(() => {
    if (currentThreadRef.current !== threadKey) {
      currentThreadRef.current = threadKey;
      positionedRef.current = false;
      pinnedToLatestRef.current = true;
    }

    const viewport = viewportRef.current;
    if (!viewport) return;

    if (!positionedRef.current) {
      positionedRef.current = true;
      viewport.scrollTop = viewport.scrollHeight;
      pinnedToLatestRef.current = true;
      return;
    }

    if (pinnedToLatestRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [busy, itemCount, threadKey]);

  return { viewportRef, onScroll, scrollToLatest };
}
